import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useApiState } from '../hooks/useApiState';
import './UserFormModal.css';

function AddParticipantModal({ seasonId, existingParticipants, onClose, onSave }) {
  const { data: allUsers, loading, error, setError, executeRequest } = useApiState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredUsers(allUsers);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = allUsers.filter(user =>
        user.username.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        (user.display_name && user.display_name.toLowerCase().includes(term))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, allUsers]);

  const loadUsers = async () => {
    try {
      await executeRequest(async () => {
        const response = await adminAPI.getAllUsers();

        // Filter out users who are already participants
        const existingUserIds = existingParticipants.map(p => p.user_id);
        const availableUsers = response.data.users.filter(user =>
          !existingUserIds.includes(user.id) && user.role === 'user'
        );

        setFilteredUsers(availableUsers);
        return availableUsers;
      });
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleSelectAll = () => {
    setSelectedUserIds(filteredUsers.map(u => u.id));
  };

  const handleDeselectAll = () => {
    setSelectedUserIds([]);
  };

  const handleToggleUser = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedUserIds.length === 0) {
      setError('Please select at least one user');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(selectedUserIds);
    } catch (err) {
      console.error('Save error:', err);
      setError(err.response?.data?.error || 'Failed to add participants');
      setSaving(false);
    }
  };

  const selectedCount = selectedUserIds.length;
  const filteredCount = filteredUsers.length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ padding: '1rem 1.5rem' }}>
          <h2 style={{ fontSize: '1.25rem' }}>Add Participants</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '1rem 1.5rem' }}>
            {error && (
              <div className="error-message">{error}</div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                Loading users...
              </div>
            ) : allUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                No users available to add. All users are already participants in this season.
              </div>
            ) : (
              <>
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <label htmlFor="search" style={{ marginBottom: '0.35rem' }}>Search Users</label>
                  <input
                    type="text"
                    id="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by username, email, or display name..."
                    disabled={saving}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {selectedCount} of {filteredCount} user{filteredCount !== 1 ? 's' : ''} selected
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="btn-link"
                      disabled={saving || filteredCount === 0}
                      style={{ fontSize: '0.85rem' }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAll}
                      className="btn-link"
                      disabled={saving || selectedCount === 0}
                      style={{ fontSize: '0.85rem' }}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <div className="user-checkbox-list">
                  {filteredUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      No users match your search
                    </div>
                  ) : (
                    filteredUsers.map(user => (
                      <label
                        key={user.id}
                        className="user-checkbox-item"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.4rem 0.5rem',
                          border: '1px solid #e2e8f0',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          backgroundColor: selectedUserIds.includes(user.id) ? '#f0f9ff' : 'transparent',
                          borderColor: selectedUserIds.includes(user.id) ? '#3b82f6' : '#e2e8f0'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleToggleUser(user.id)}
                          disabled={saving}
                          style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                        />
                        <div style={{ fontSize: '0.9rem', lineHeight: '1.3' }}>
                          <span style={{ fontWeight: '500' }}>{user.username}</span>
                          <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>
                            ({user.display_name || user.email})
                          </span>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                <p className="form-note" style={{ marginTop: '0.5rem', marginBottom: '0' }}>
                  Selected users will be enrolled with today's date and is_active = true.
                </p>
              </>
            )}
          </div>

          <div className="modal-footer" style={{ padding: '1rem 1.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-cancel"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || selectedCount === 0}
            >
              {saving ? 'Adding...' : `Add ${selectedCount} Participant${selectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddParticipantModal;
